export type DataLayerProductInput = {
  id?: string | number;
  code?: string | number;
  sku?: string | number;
  name?: string;
  list?: string;
  position?: number | string;
  price?: number | string;
  priceOld?: number | string;
  quantity?: number | string;
  brand?: string;
  category?: string | string[];
  attributes?: Record<string, unknown>;
  attribute_values?: Record<string, unknown>;
  commerce?: any;
  taxonomy?: any;
};

export type EcommerceProduct = {
  id: string;
  name: string;
  price?: number;
  brand?: string;
  category?: string;
  variant?: string;
  quantity: number;
  list?: string;
  position?: number;
};

type DataLayerOptions = {
  currencyCode?: string;
  fallbackProduct?: DataLayerProductInput;
  step?: number;
  orderId?: string;
  revenue?: number;
  tax?: number;
  shipping?: number;
  coupon?: string;
};

type PurchaseInfo = {
  id?: string;
  orderId?: string;
  order_id?: string;
  revenue?: number;
  tax?: number;
  shipping?: number;
  coupon?: string;
};

const DEFAULT_CURRENCY = "RUB";

const toSafeString = (value?: string | number): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const normalized = typeof value === "string" ? value : String(value);
  const trimmed = normalized.trim();
  return trimmed || undefined;
};

const normalizePriceValue = (value: unknown): number | undefined => {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) {
    return undefined;
  }
  // Product prices use the same main-currency units as the catalog UI.
  return num;
};

const extractCategory = (source?: DataLayerProductInput): string | undefined => {
  if (!source) return undefined;

  const taxonomy = source.taxonomy;
  const chain = taxonomy?.chain;
  if (Array.isArray(chain) && chain.length) {
    return chain.filter(Boolean).join(" / ");
  }
  const category = taxonomy?.category ?? source.category;
  if (Array.isArray(category)) {
    return category.filter(Boolean).join(" / ");
  }
  if (typeof category === "string" && category.trim()) {
    return category.trim();
  }
  return undefined;
};

const buildVariantString = (
  attrs?: Record<string, unknown> | null
): string | undefined => {
  if (!attrs || typeof attrs !== "object") {
    return undefined;
  }
  const parts = Object.entries(attrs)
    .map(([key, value]) => {
      const valueStr =
        typeof value === "string"
          ? value
          : value !== null && value !== undefined
          ? String(value)
          : "";
      if (!valueStr) return null;
      return `${key}: ${valueStr}`;
    })
    .filter(Boolean) as string[];
  return parts.length ? parts.join(", ") : undefined;
};

const mapProductToEcommerce = (
  product: DataLayerProductInput,
  fallback?: DataLayerProductInput
): EcommerceProduct | null => {
  const id =
    toSafeString(product.id ?? product.code ?? product.sku) ||
    toSafeString(fallback?.id ?? fallback?.code ?? fallback?.sku);
  if (!id) {
    return null;
  }

  const name =
    toSafeString(product.name) ??
    toSafeString(fallback?.name) ??
    "Товар";

  const price =
    normalizePriceValue(
      product.price ??
        product.priceOld
    ) ??
    normalizePriceValue(
      fallback?.price ??
        fallback?.priceOld
    );

  const quantityRaw = Number(
    product.quantity ?? fallback?.quantity ?? 1
  );
  const quantity = Number.isFinite(quantityRaw) && quantityRaw > 0 ? quantityRaw : 1;

  const brand =
    toSafeString(product.brand) ??
    toSafeString(fallback?.brand) ??
    undefined;

  const category = extractCategory(product) ?? extractCategory(fallback);

  const variant =
    buildVariantString(product.attributes ?? product.attribute_values) ??
    buildVariantString(fallback?.attributes ?? fallback?.attribute_values);

  const list =
    toSafeString(product.list) ??
    toSafeString(fallback?.list);

  const positionRaw = Number(product.position ?? fallback?.position);
  const position =
    Number.isFinite(positionRaw) && positionRaw > 0 ? positionRaw : undefined;

  return {
    id,
    name,
    price,
    brand,
    category,
    variant,
    quantity,
    list,
    position,
  };
};

const toProductList = (
  products: DataLayerProductInput | DataLayerProductInput[],
  fallback?: DataLayerProductInput
): EcommerceProduct[] => {
  const list = Array.isArray(products) ? products : [products];
  return list
    .map((item) => mapProductToEcommerce(item, fallback))
    .filter(Boolean) as EcommerceProduct[];
};

const pushDataLayer = (
  event: string,
  ecommerce: Record<string, unknown>
) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const dl = (window as any).dataLayer || [];
    (window as any).dataLayer = dl;
    dl.push({
      event,
      ecommerce,
    });
  } catch (error) {
    console.warn("dataLayer push failed", error);
  }
};

const pushEcommerceObject = (ecommerce: Record<string, unknown>) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const dl = (window as any).dataLayer || [];
    (window as any).dataLayer = dl;
    dl.push({ ecommerce });
  } catch (error) {
    console.warn("dataLayer push failed", error);
  }
};

export const pushProductDetailEvent = (
  product: DataLayerProductInput,
  options?: DataLayerOptions
) => {
  const products = toProductList(product, options?.fallbackProduct);
  if (!products.length) return;

  pushDataLayer("productDetail", {
    currencyCode: options?.currencyCode || DEFAULT_CURRENCY,
    detail: { products },
  });
};

type ListEventOptions = DataLayerOptions & {
  listName?: string;
  startPosition?: number;
};

const applyListContext = (
  products: EcommerceProduct[],
  options?: ListEventOptions
) => {
  const start = options?.startPosition ?? 1;
  return products.map((item, index) => ({
    ...item,
    list: item.list ?? options?.listName,
    position: item.position ?? start + index,
  }));
};

export const pushProductImpressionEvent = (
  products: DataLayerProductInput | DataLayerProductInput[],
  options?: ListEventOptions
) => {
  const mapped = toProductList(products, options?.fallbackProduct);
  if (!mapped.length) return;

  const withList = applyListContext(mapped, options);

  // Яндекс.Метрика ожидает структуру ecommerce без обязательного поля event
  pushEcommerceObject({
    currencyCode: options?.currencyCode || DEFAULT_CURRENCY,
    impressions: withList,
  });
};

export const pushProductClickEvent = (
  products: DataLayerProductInput | DataLayerProductInput[],
  options?: ListEventOptions
) => {
  const mapped = toProductList(products, options?.fallbackProduct);
  if (!mapped.length) return;

  const withList = applyListContext(mapped, options);

  // Яндекс.Метрика ожидает структуру ecommerce без обязательного поля event
  pushEcommerceObject({
    currencyCode: options?.currencyCode || DEFAULT_CURRENCY,
    click: { products: withList },
  });
};

export const pushAddToCartEvent = (
  product: DataLayerProductInput | DataLayerProductInput[],
  options?: DataLayerOptions
) => {
  const products = toProductList(product, options?.fallbackProduct);
  if (!products.length) return;

  pushDataLayer("addToCart", {
    currencyCode: options?.currencyCode || DEFAULT_CURRENCY,
    add: { products },
  });
};

export const pushRemoveFromCartEvent = (
  product: DataLayerProductInput | DataLayerProductInput[],
  options?: DataLayerOptions
) => {
  const products = toProductList(product, options?.fallbackProduct);
  if (!products.length) return;

  pushDataLayer("removeFromCart", {
    currencyCode: options?.currencyCode || DEFAULT_CURRENCY,
    remove: { products },
  });
};

export const pushCheckoutEvent = (
  products: DataLayerProductInput | DataLayerProductInput[],
  options?: DataLayerOptions
) => {
  const mapped = toProductList(products, options?.fallbackProduct);
  if (!mapped.length) return;

  pushDataLayer("checkout", {
    currencyCode: options?.currencyCode || DEFAULT_CURRENCY,
    checkout: {
      actionField: { step: options?.step ?? 1 },
      products: mapped,
    },
  });
};

export const pushPurchaseEvent = (
  purchase: PurchaseInfo | null,
  products: DataLayerProductInput | DataLayerProductInput[],
  options?: DataLayerOptions
) => {
  const mapped = toProductList(products, options?.fallbackProduct);
  if (!mapped.length) return;

  const actionField: Record<string, unknown> = {};
  const orderId =
    purchase?.id ??
    purchase?.orderId ??
    purchase?.order_id ??
    options?.orderId;
  if (orderId) {
    actionField.id = orderId;
  }
  const revenue =
    options?.revenue ??
    purchase?.revenue ??
    mapped.reduce((acc, item) => {
      if (typeof item.price !== "number") return acc;
      return acc + item.price * (item.quantity || 1);
    }, 0);
  if (revenue !== undefined) {
    actionField.revenue = revenue;
  }
  if (options?.tax ?? purchase?.tax) {
    actionField.tax = options?.tax ?? purchase?.tax;
  }
  if (options?.shipping ?? purchase?.shipping) {
    actionField.shipping = options?.shipping ?? purchase?.shipping;
  }
  if (options?.coupon ?? purchase?.coupon) {
    actionField.coupon = options?.coupon ?? purchase?.coupon;
  }

  pushDataLayer("purchase", {
    currencyCode: options?.currencyCode || DEFAULT_CURRENCY,
    purchase: {
      actionField,
      products: mapped,
    },
  });
};
